import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { DataWizardAct, LastStepVM, ParentVM, SectionVM } from 'civil-status/view-models/step-form';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { SectionWizardUseCase } from 'shell/models/subjects-wizard';
import Section from '../section';
import { FormikProps } from 'formik';
import { KeySections, KeySubjects } from 'civil-status/constants/keys-subjects-section';
import { sectionIsVisibility } from 'civil-status/utils/section-is-visibility';
import { ItemVM } from 'shell/view-models/item';

interface SectionsProps {
  formContext: FormikProps<LastStepVM>;
  onSaveSection: (values: SectionVM) => ResponsePromise<DataWizardAct> | void | undefined;
  sezioniUseCaseData?: Array<SectionWizardUseCase>;
  tipoEvento?: string;
  idUseCase?: string;
  idEvento?: number;
  dateFormationAct?: string;
  disabled?: boolean;
  isLastStepEmergency?: boolean;
  setDataInterprete: (isWithInterprete?: boolean) => void;
  controllSentenceRecognition: (sentenceRecognition?: string) => void;
  controllSentenceRecognitionNameSurname: (sentenceRecognition?: string) => void;
  controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence: (
    typologyConsentAndConfiguration?: ItemVM,
    typeDeclaration?: string,
    parentRecognition?: string,
    isIncestuousSonSentence?: boolean
  ) => void;
  controllIsPaternitySentence: (isPaternitySentence?: boolean) => void;
  controllParentTransmittedCitizenship: (parentTransmittedCitizenship?: string) => void;
  controllParentRecognitionMadrePadreRiconoscimento: (parentRecognition?: string) => void;
  controllParentChangedSurname: (parentChangedSurname?: string) => void;
  controllApplicant: (applicant?: string) => void;
  controllMeasureExchangeSurname: (measureExchangeSurname?: string) => void;
  controllOpposition: (opposition?: string) => void;
  controllGenericImpediment: (genericImpediment?: string) => void;
  controllImpedimentUnitedCivilly: (impedimentUnitedCivilly?: string) => void;
  controllPublication: (publication?: string) => void;
  controllMinorGroomAssistance: (minorGroomAssistance?: string) => void;
  controllMinorWifeAssistance: (minorWifeAssistance?: string) => void;
  controllAgeDefect: (ageDefect?: string) => void;
  controllTemporaryBanRemarriage: (isTemporaryBanRemarriage?: boolean) => void;
  controllRelationship: (relationship?: string) => void;
  controllNumberWitnesses: (numberWitnesses?: string) => void;
  controllPropertyRegime: (propertyRegime?: string) => void;
  controllLawyerHusbandWife: (isLawyerHusband?: boolean, isLawyerWife?: boolean) => void;
  controllTypeSeparation: (typeSeparation?: string) => void;
  controllIsLawyerHusbandWife: (isLawyerHusbandWife?: boolean) => void;
  controllIsReceivedPrivateCitizen: (isReceivedPrivateCitizen?: boolean) => void;
  controllIsSonUnderFourteen: (isSonUnderFourteen?: boolean) => void;
  controllIsReceivedByPost: (isReceivedByPost?: boolean) => void;
  controllTypeApplicant: (typeApplicant?: string) => void;
  controllNumberHolders: (numberHolders?: string) => void;
  controllNumberChildren: (numberChildren?: string) => void;
}

const SectionsComponent = ({
  formContext,
  onSaveSection,
  sezioniUseCaseData,
  tipoEvento,
  idUseCase,
  dateFormationAct,
  idEvento,
  disabled = false,
  isLastStepEmergency = false,
  setDataInterprete,
  controllSentenceRecognition,
  controllSentenceRecognitionNameSurname,
  controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence,
  controllIsPaternitySentence,
  controllParentTransmittedCitizenship,
  controllParentRecognitionMadrePadreRiconoscimento,
  controllParentChangedSurname,
  controllApplicant,
  controllMeasureExchangeSurname,
  controllOpposition,
  controllGenericImpediment,
  controllImpedimentUnitedCivilly,
  controllPublication,
  controllMinorGroomAssistance,
  controllMinorWifeAssistance,
  controllAgeDefect,
  controllTemporaryBanRemarriage,
  controllRelationship,
  controllNumberWitnesses,
  controllPropertyRegime,
  controllLawyerHusbandWife,
  controllTypeSeparation,
  controllIsLawyerHusbandWife,
  controllIsReceivedPrivateCitizen,
  controllIsSonUnderFourteen,
  controllIsReceivedByPost,
  controllTypeApplicant,
  controllNumberHolders,
  controllNumberChildren,
}: SectionsProps): React.ReactElement => {
  const [sezioniUseCaseDataArr, setSezioniUseCaseDataArr] = useState<Array<SectionWizardUseCase>>(
    []
  );

  // values Dati generali consenso
  const valuesDatiGeneraliConsenso: SectionVM | undefined = useMemo(() => {
    return (
      formContext.values[KeySections.keyDatiGeneraliConsAssRico] ||
      formContext.values[KeySections.keyDatiGeneraliAssRico] ||
      formContext.values[KeySections.keyDatiGeneraliPresDoc] ||
      formContext.values[KeySections.keyDatiGeneraliConsRico] ||
      formContext.values[KeySections.keyDatiGeneraliConsRicoEvento] ||
      formContext.values[KeySections.keyDatiGeneraliConsAssCognridRico] ||
      formContext.values[KeySections.keyDatiGeneraliConsRicoSent] ||
      formContext.values[KeySections.keyDatiGeneraliRiconoscimento] ||
      formContext.values[KeySections.keyDatiGeneraliRicoGen] ||
      formContext.values[KeySections.keyDatiGeneraliConsenso] ||
      formContext.values[KeySections.keyDatiGeneraliAssRicoP]
    );
  }, [
    formContext.values[KeySections.keyDatiGeneraliConsAssRico],
    formContext.values[KeySections.keyDatiGeneraliAssRico],
    formContext.values[KeySections.keyDatiGeneraliPresDoc],
    formContext.values[KeySections.keyDatiGeneraliConsRico],
    formContext.values[KeySections.keyDatiGeneraliConsRicoEvento],
    formContext.values[KeySections.keyDatiGeneraliConsAssCognridRico],
    formContext.values[KeySections.keyDatiGeneraliConsRicoSent],
    formContext.values[KeySections.keyDatiGeneraliRiconoscimento],
    formContext.values[KeySections.keyDatiGeneraliRicoGen],
    formContext.values[KeySections.keyDatiGeneraliConsenso],
    formContext.values[KeySections.keyDatiGeneraliAssRicoP],
  ]);

  // values sezione dati trascrizione
  const valuesDatiTrascrizione: SectionVM | undefined = useMemo(() => {
    return (
      formContext.values[KeySections.keyDatiTrascrizione] ||
      formContext.values[KeySections.keyDatiTrascrizioneTpRichiedente]
    );
  }, [
    formContext.values[KeySections.keyDatiTrascrizione],
    formContext.values[KeySections.keyDatiTrascrizioneTpRichiedente],
  ]);

  // values sezione Dati Specifici Di Cittadinanza
  const valuesDatiSpecificiCittadinanza: SectionVM = useMemo(() => {
    return formContext.values[KeySections.keyDatiSpecificiCittadinanza] || {};
  }, [formContext.values[KeySections.keyDatiSpecificiCittadinanza]]);

  // values soggetto intestatario nascituro
  const valuesIntNas: ParentVM | undefined = useMemo(() => {
    return (
      formContext.values[KeySubjects.keyIntNas] ||
      formContext.values[KeySubjects.keyIntNas2] ||
      formContext.values[KeySubjects.keyIntNas2C1]
    );
  }, [
    formContext.values[KeySubjects.keyIntNas],
    formContext.values[KeySubjects.keyIntNas2],
    formContext.values[KeySubjects.keyIntNas2C1],
  ]);

  const valuesDeceduto: ParentVM | undefined = useMemo(() => {
    return formContext.values[KeySubjects.keyDeceduto];
  }, [formContext.values[KeySubjects.keyDeceduto]]);

  const valuesSpecificCivilUnionMarriage: SectionVM = useMemo(() => {
    return (
      formContext.values[KeySections.keyDatiSpecificiUniciv] ||
      formContext.values[KeySections.keyDatiSpecificiMatr] ||
      {}
    );
  }, [
    formContext.values[KeySections.keyDatiSpecificiUniciv],
    formContext.values[KeySections.keyDatiSpecificiMatr],
  ]);

  const valuesRegimePatrimoniale: SectionVM = useMemo(() => {
    return formContext.values[KeySections.keyRegimePatrimoniale] || {};
  }, [formContext.values[KeySections.keyRegimePatrimoniale]]);

  const valuesDatiSepar: SectionVM = useMemo(() => {
    return (
      formContext.values[KeySections.keyDatiSepar] ||
      formContext.values[KeySections.keyDatiTrascrizioneSeparazione] ||
      {}
    );
  }, [
    formContext.values[KeySections.keyDatiSepar],
    formContext.values[KeySections.keyDatiTrascrizioneSeparazione],
  ]);

  const valuesDatiRiconciliazione: SectionVM = useMemo(() => {
    return formContext.values[KeySections.keyDatiRiconciliazione] || {};
  }, [formContext.values[KeySections.keyDatiRiconciliazione]]);

  const valuesTrascrizioneCittadinanza: SectionVM = useMemo(() => {
    return formContext.values[KeySections.keyTrascrizioneCittadinanza] || {};
  }, [formContext.values[KeySections.keyTrascrizioneCittadinanza]]);

  useEffect(() => {
    if ((sezioniUseCaseData || [])?.length === 0) {
      return;
    }

    setSezioniUseCaseDataArr(
      (sezioniUseCaseData || []).filter((itemSection) => {
        return sectionIsVisibility(
          itemSection,
          valuesDatiGeneraliConsenso,
          valuesDatiSpecificiCittadinanza,
          valuesIntNas,
          valuesDatiTrascrizione,
          valuesSpecificCivilUnionMarriage,
          valuesRegimePatrimoniale,
          valuesDatiSepar,
          valuesDatiRiconciliazione,
          valuesTrascrizioneCittadinanza,
          valuesDeceduto,
          isLastStepEmergency
        );
      })
    );
  }, [
    sezioniUseCaseData,
    valuesDatiGeneraliConsenso,
    valuesDatiSpecificiCittadinanza,
    valuesIntNas,
    valuesDatiTrascrizione,
    valuesSpecificCivilUnionMarriage,
    valuesRegimePatrimoniale,
    valuesDatiSepar,
    valuesDatiRiconciliazione,
    valuesTrascrizioneCittadinanza,
    valuesDeceduto,
    isLastStepEmergency,
  ]);

  return (
    <Fragment>
      {sezioniUseCaseDataArr.map((itemSection) => {
        const keySection = `section-${itemSection.key?.codSezione?.trim()}`;

        return (
          <Section
            formContext={formContext}
            title={itemSection.nomeSezione}
            keySection={keySection}
            configuration={itemSection.configurazione?.trim()}
            presenza={itemSection.presenza}
            key={keySection}
            codSection={itemSection.key?.codSezione?.trim() || ''}
            onSaveSection={onSaveSection}
            disabled={disabled}
            tipoEvento={tipoEvento}
            idUseCase={idUseCase}
            dateFormationAct={dateFormationAct}
            idEvento={idEvento}
            isLastStepEmergency={isLastStepEmergency}
            setDataInterprete={setDataInterprete}
            controllSentenceRecognition={controllSentenceRecognition}
            controllSentenceRecognitionNameSurname={controllSentenceRecognitionNameSurname}
            controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence={
              controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence
            }
            controllIsPaternitySentence={controllIsPaternitySentence}
            controllParentTransmittedCitizenship={controllParentTransmittedCitizenship}
            controllParentRecognitionMadrePadreRiconoscimento={
              controllParentRecognitionMadrePadreRiconoscimento
            }
            controllParentChangedSurname={controllParentChangedSurname}
            controllApplicant={controllApplicant}
            controllMeasureExchangeSurname={controllMeasureExchangeSurname}
            controllOpposition={controllOpposition}
            controllGenericImpediment={controllGenericImpediment}
            controllImpedimentUnitedCivilly={controllImpedimentUnitedCivilly}
            controllPublication={controllPublication}
            controllMinorGroomAssistance={controllMinorGroomAssistance}
            controllMinorWifeAssistance={controllMinorWifeAssistance}
            controllAgeDefect={controllAgeDefect}
            controllTemporaryBanRemarriage={controllTemporaryBanRemarriage}
            controllRelationship={controllRelationship}
            controllLawyerHusbandWife={controllLawyerHusbandWife}
            controllTypeSeparation={controllTypeSeparation}
            controllIsLawyerHusbandWife={controllIsLawyerHusbandWife}
            controllIsReceivedPrivateCitizen={controllIsReceivedPrivateCitizen}
            controllIsSonUnderFourteen={controllIsSonUnderFourteen}
            controllNumberWitnesses={controllNumberWitnesses}
            controllPropertyRegime={controllPropertyRegime}
            controllIsReceivedByPost={controllIsReceivedByPost}
            controllTypeApplicant={controllTypeApplicant}
            controllNumberHolders={controllNumberHolders}
            controllNumberChildren={controllNumberChildren}
          />
        );
      })}
    </Fragment>
  );
};

SectionsComponent.displayName = 'Sections Component';

const Sections = memo(SectionsComponent);

export default Sections;
